import { PDFDocument, StandardFonts, degrees, rgb, EncryptedPDFError, type PDFPage } from 'pdf-lib';
import type { PageItem, TextBox } from './types';
import { applyStamps, type StampOptions } from './stamps';

/**
 * The PDF engine, running in the browser.
 *
 * This is the same page-plan model the server used, moved client-side: the
 * documents never leave the machine, and every page operation works with no
 * network at all. Office conversion is the one exception and still needs the
 * server, because it needs LibreOffice.
 */

export class PdfError extends Error {}

/**
 * Document properties written into the exported PDF.
 *
 * These are what a reader shows under "Document Properties", and what most
 * search and document-management systems index on, so they are worth filling in
 * rather than leaving as whatever the first source file happened to carry.
 */
export interface PdfMetadata {
  title?: string;
  author?: string;
  subject?: string;
  keywords?: string;
}

const VALID_ROTATIONS = new Set([0, 90, 180, 270]);

/** Guards against a malformed or hostile file exhausting the tab. */
const MAX_PAGES_PER_DOCUMENT = 2000;
const MAX_PAGES_PER_EXPORT = 5000;

export async function loadPdf(bytes: Uint8Array): Promise<PDFDocument> {
  try {
    return await PDFDocument.load(bytes, { updateMetadata: false });
  } catch (err) {
    if (err instanceof EncryptedPDFError) {
      throw new PdfError(
        'This PDF is password-protected. Remove the password in the original application, then add it again.',
      );
    }
    throw new PdfError('This file could not be read as a PDF. It may be corrupt or incomplete.');
  }
}

/**
 * `PDFDocument.load` accepts structurally broken files and only fails when the
 * page tree is first walked, so this read has to be guarded too.
 */
export function pageCountOf(pdf: PDFDocument): number {
  try {
    return pdf.getPageCount();
  } catch {
    throw new PdfError('This PDF’s page structure is damaged and could not be read.');
  }
}

export async function countPages(bytes: Uint8Array): Promise<number> {
  const count = pageCountOf(await loadPdf(bytes));
  if (count === 0) throw new PdfError('This PDF contains no pages.');
  if (count > MAX_PAGES_PER_DOCUMENT) {
    throw new PdfError(`This PDF has ${count} pages, above the ${MAX_PAGES_PER_DOCUMENT}-page limit.`);
  }
  return count;
}

/**
 * Builds one PDF from an ordered list of pages that may span any number of
 * sources. Pages from the same source are copied in a single batch, so a large
 * merge parses each source exactly once.
 */
export async function assemble(
  pages: PageItem[],
  bytesFor: (docId: string) => Uint8Array | undefined,
  metadata: PdfMetadata = {},
  stamps: StampOptions = {},
): Promise<Uint8Array> {
  if (pages.length === 0) throw new PdfError('An output file must contain at least one page.');
  if (pages.length > MAX_PAGES_PER_EXPORT) {
    throw new PdfError(`Exports are limited to ${MAX_PAGES_PER_EXPORT} pages.`);
  }

  // Group by source document, remembering each page's slot in that document's
  // batch so a page used twice stays two distinct copies. Blank pages have no
  // source, so they claim no slot.
  const indicesByDoc = new Map<string, number[]>();
  const slots: Array<{ docId: string; slot: number } | null> = [];
  for (const page of pages) {
    if (!VALID_ROTATIONS.has(((page.rotation % 360) + 360) % 360)) {
      throw new PdfError(`Unsupported rotation: ${page.rotation}. Use 0, 90, 180, or 270.`);
    }
    if (page.blank) {
      slots.push(null);
      continue;
    }
    let indices = indicesByDoc.get(page.docId);
    if (!indices) {
      indices = [];
      indicesByDoc.set(page.docId, indices);
    }
    slots.push({ docId: page.docId, slot: indices.length });
    indices.push(page.pageIndex);
  }

  const out = await PDFDocument.create({ updateMetadata: false });
  const copiedByDoc = new Map<string, Awaited<ReturnType<PDFDocument['copyPages']>>>();

  for (const [docId, indices] of indicesByDoc) {
    const bytes = bytesFor(docId);
    if (!bytes) throw new PdfError('One of the selected files is no longer available.');
    const source = await loadPdf(bytes);
    const sourcePageCount = pageCountOf(source);
    for (const index of indices) {
      if (!Number.isInteger(index) || index < 0 || index >= sourcePageCount) {
        throw new PdfError(`Page ${index + 1} does not exist in one of the selected documents.`);
      }
    }
    copiedByDoc.set(docId, await out.copyPages(source, indices));
  }

  /*
   * A blank page takes the size of the nearest real page — the one before it
   * where there is one, otherwise the one after. Inserting a blank into a set
   * of A4 pages should give an A4 blank, not a Letter one.
   */
  const sizeAt = (index: number): [number, number] | null => {
    const slot = slots[index];
    if (!slot) return null;
    const page = copiedByDoc.get(slot.docId)?.[slot.slot];
    if (!page) return null;
    const { width, height } = page.getSize();
    return [width, height];
  };
  const nearestSize = (index: number): [number, number] => {
    for (let i = index - 1; i >= 0; i -= 1) {
      const size = sizeAt(i);
      if (size) return size;
    }
    for (let i = index + 1; i < slots.length; i += 1) {
      const size = sizeAt(i);
      if (size) return size;
    }
    return [612, 792]; // US Letter, when every page is blank.
  };

  // Sequential rather than forEach: each page may need a font embedded, which
  // is async, and a blank page still has to land in the right position.
  for (let i = 0; i < pages.length; i += 1) {
    const pageRef = pages[i]!;
    const slot = slots[i];
    const page = slot
      ? copiedByDoc.get(slot.docId)![slot.slot]!
      : out.addPage(nearestSize(i));

    if (slot) {
      if (pageRef.rotation !== 0) {
        const existing = page.getRotation().angle;
        page.setRotation(degrees((((existing + pageRef.rotation) % 360) + 360) % 360));
      }
      out.addPage(page);
    }

    // After the rotation, since positions are in what the reader sees.
    if (pageRef.texts?.length) await drawTextBoxes(out, page, pageRef.texts);
  }

  // After every page is in place, so {N} counts the finished document and a
  // watermark lands on the pages that actually made it.
  await applyStamps(out, stamps);

  applyMetadata(out, metadata);
  return out.save({ useObjectStreams: true });
}

/**
 * Writes the document properties.
 *
 * Only fields the person actually filled in are set: an empty string would
 * otherwise show as a blank Title in a reader, which reads as a broken document
 * rather than an unset one.
 */
function applyMetadata(pdf: PDFDocument, metadata: PdfMetadata): void {
  const title = metadata.title?.trim();
  const author = metadata.author?.trim();
  const subject = metadata.subject?.trim();

  if (title) pdf.setTitle(title);
  if (author) pdf.setAuthor(author);
  if (subject) pdf.setSubject(subject);

  // setKeywords takes an array and joins it with SPACES, which would turn
  // "finance, q4, 2026" into "finance q4 2026" and lose the separation that
  // indexers and search rely on. Normalising and passing one element keeps the
  // conventional comma-separated form intact.
  const keywords = (metadata.keywords ?? '')
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);
  if (keywords.length > 0) pdf.setKeywords([keywords.join(', ')]);

  /*
   * Creator carries the author's name when there is one.
   *
   * macOS reads its "Authors" row from the PDF Creator field, not Author —
   * verified with a file whose three fields held distinct values. Leaving the
   * app name here meant every Mac user saw "Authors: ON Services PDF Toolkit"
   * and reasonably concluded the author had not been saved, when it had.
   *
   * Author is still set for every reader that looks at the right field, and
   * Producer stays as this app, which is what the spec means by it: the thing
   * that produced the PDF.
   */
  pdf.setCreator(author || 'ON Services PDF Toolkit');
  pdf.setProducer('ON Services PDF Toolkit');
  pdf.setCreationDate(new Date());
  pdf.setModificationDate(new Date());
}

/** Builds a PDF from one or more raster images, one image per page. */
export async function imagesToPdf(
  images: Array<{ bytes: Uint8Array; mimeType: string }>,
): Promise<Uint8Array> {
  const pdf = await PDFDocument.create({ updateMetadata: false });
  for (const image of images) {
    const embedded =
      image.mimeType === 'image/png'
        ? await pdf.embedPng(image.bytes)
        : await pdf.embedJpg(image.bytes);
    const page = pdf.addPage([embedded.width, embedded.height]);
    page.drawImage(embedded, { x: 0, y: 0, width: embedded.width, height: embedded.height });
  }
  pdf.setProducer('ON Services PDF Toolkit');
  return pdf.save({ useObjectStreams: true });
}

/**
 * Draws the text added to a page.
 *
 * Positions arrive in the coordinates the reader sees, so they are mapped back
 * into the page's own space — a quarter turn swaps the axes, and the text is
 * counter-rotated so it stays upright whichever way the page is turned.
 */
/**
 * The 14 fonts every PDF reader has built in. Using these means nothing is
 * embedded, the file stays small, and the text renders identically everywhere.
 */
const STANDARD_FONTS = {
  helvetica: {
    regular: StandardFonts.Helvetica,
    bold: StandardFonts.HelveticaBold,
    italic: StandardFonts.HelveticaOblique,
    boldItalic: StandardFonts.HelveticaBoldOblique,
  },
  times: {
    regular: StandardFonts.TimesRoman,
    bold: StandardFonts.TimesRomanBold,
    italic: StandardFonts.TimesRomanItalic,
    boldItalic: StandardFonts.TimesRomanBoldItalic,
  },
  courier: {
    regular: StandardFonts.Courier,
    bold: StandardFonts.CourierBold,
    italic: StandardFonts.CourierOblique,
    boldItalic: StandardFonts.CourierBoldOblique,
  },
} as const;

function standardFontFor(box: TextBox): StandardFonts {
  const family = STANDARD_FONTS[box.font ?? 'helvetica'];
  if (box.bold && box.italic) return family.boldItalic;
  if (box.bold) return family.bold;
  if (box.italic) return family.italic;
  return family.regular;
}

/** #rrggbb to pdf-lib's 0-1 components; black for anything unparseable. */
function parseColor(hex: string | undefined) {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex ?? '');
  if (!match) return rgb(0, 0, 0);
  const value = parseInt(match[1]!, 16);
  return rgb(((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255);
}

async function drawTextBoxes(pdf: PDFDocument, page: PDFPage, boxes: TextBox[]): Promise<void> {
  const usable = boxes.filter((box) => box.text.trim().length > 0);
  if (usable.length === 0) return;

  // One embed per distinct face, not per box.
  const fonts = new Map<StandardFonts, Awaited<ReturnType<PDFDocument['embedFont']>>>();
  for (const box of usable) {
    const name = standardFontFor(box);
    if (!fonts.has(name)) fonts.set(name, await pdf.embedFont(name));
  }
  const { width, height } = page.getSize();
  const rotation = ((page.getRotation().angle % 360) + 360) % 360;
  const quarter = rotation % 180 === 90;
  const displayedWidth = quarter ? height : width;
  const displayedHeight = quarter ? width : height;

  const toPage = (dx: number, dy: number): { x: number; y: number } => {
    switch (rotation) {
      case 90:
        return { x: width - dy, y: dx };
      case 180:
        return { x: width - dx, y: height - dy };
      case 270:
        return { x: dy, y: height - dx };
      default:
        return { x: dx, y: dy };
    }
  };

  for (const box of usable) {
    /*
     * A box carries its own turn, clockwise from upright as the reader sees it.
     * Everything measured from the box's anchor — the baseline drop and the
     * stacking of further lines — has to follow that turn, or a tilted block
     * would come apart into lines marching off in the untilted direction.
     *
     * Displayed space has y running down, where a clockwise turn sends the
     * local "down" axis to (-sin, cos).
     */
    const turn = (((box.angle ?? 0) % 360) + 360) % 360;
    const radians = (turn * Math.PI) / 180;
    const downX = -Math.sin(radians);
    const downY = Math.cos(radians);
    const anchorX = box.x * displayedWidth;
    const anchorY = box.y * displayedHeight;

    // Multi-line text is drawn line by line: pdf-lib does not wrap, and a
    // newline inside one drawText call is not laid out.
    box.text.split('\n').forEach((line, index) => {
      if (!line.trim()) return;
      // The baseline sits below the top of the text by roughly its ascender.
      const drop = box.size * 0.8 + index * box.size * 1.2;
      // Back to y-up, which is how PDF counts.
      const { x, y } = toPage(
        anchorX + drop * downX,
        displayedHeight - (anchorY + drop * downY),
      );
      page.drawText(line, {
        x,
        y,
        size: box.size,
        font: fonts.get(standardFontFor(box))!,
        color: parseColor(box.color),
        // pdf-lib turns counter-clockwise; the box's own turn is clockwise.
        rotate: degrees(rotation - turn),
      });
    });
  }
}

/** Replaces characters that are awkward in a download filename. */
export function safeFileName(name: string, fallback = 'document'): string {
  const base = name.replace(/\.pdf$/i, '').replace(/[^\w \-().]+/g, '_').trim().slice(0, 120);
  return `${base || fallback}.pdf`;
}
